import type { MispricedGridCell } from "../api/types";
import { zWord } from "./zscore";

/**
 * Разбор сетки порога кромки для экрана.
 *
 * # Какой вопрос закрывает таблица
 *
 * Порог `MISPRICED_TAKER_MARGIN` в `.env.example` подписан прямым текстом как
 * «отправная точка оператора, а не замер», и первый же вопрос по панели
 * расхождения — не мал ли он. Сводка ответить не может: она считает один порог,
 * тот, что стоял.
 *
 * # Почему ступень НИКОГДА не показывается одним числом
 *
 * Замер на боевой базе 2026-09-01, 11 400 сведённых тейкерских строк. Сетка без
 * разреза по возрасту снимка растёт монотонно: ROI +6 % на ступени 0.02 и
 * +62 % на 0.20. Читается это как «порог мал». С разрезом весь рост оказывается
 * в снимках старше 200 мс, а на свежих каждая ступень отрицательна и портится с
 * ростом порога: −5.8 % на 0.02 и −25.7 % на 0.20.
 *
 * Механика: порог отбирает не кромку, а нашу ЗАДЕРЖКУ. Доля снимков старше
 * 200 мс среди срабатываний растёт с 5.6 % на ступени 0.02 до 49.1 % на 0.20 —
 * чем больше кромки просишь, тем чаще её даёт котировка, которой на бирже уже
 * нет. Ровно тот же артефакт, что у почти решённого (ADR-0022, решение 10).
 *
 * Поэтому здесь нет функции «ступень целиком»: сложить корзины в одно число
 * значило бы вынести этот обман на экран. Складывать их не даёт и контракт.
 */

/** Число из строки контракта. null означает прочерк, а не ноль. */
function num(v: string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Корзины возраста снимка в том порядке, в каком их читают: от свежих. */
export const FRESHNESS = ["live", "warm", "stale", "unknown"] as const;

export type Freshness = (typeof FRESHNESS)[number];

/**
 * Имя корзины словами.
 *
 * У свежей граница названа числом не для красоты: 50 мс — это
 * `MISPRICED_LIVE_MAX_BOOK_AGE`, то есть порог, за которым живой вход
 * отказывается ставить ордер вовсе. Строки свежее неё — единственные, по
 * которым панель вправе судить о деньгах.
 */
export function freshLabel(f: string): string {
  if (f === "live") return "свежий < 50 мс";
  if (f === "warm") return "50–200 мс";
  if (f === "stale") return "протух > 200 мс";
  if (f === "unknown") return "возраст неизвестен";
  return f;
}

/** Пояснение к корзине — тем, кто смотрит на таблицу впервые. */
export function freshHint(f: string): string {
  if (f === "live") {
    return "живой вход берёт только такие снимки: по ним одним и можно судить о деньгах";
  }
  if (f === "warm") {
    return "живой вход отказал бы по правилу stale_book — счёт здесь уже смещён";
  }
  if (f === "stale") {
    return "счёт на таких снимках принадлежит нашей задержке, а не рынку: " +
      "мы «покупаем» по цене, которой на бирже уже нет";
  }
  return "возраста снимка нет. Отдельной корзиной: неизвестности нельзя приписывать лучшую";
}

/** Ступени сетки по возрастанию — без повторов и без пустых. */
export function stepsOf(cells: MispricedGridCell[]): string[] {
  const seen = new Map<number, string>();
  for (const c of cells) {
    const n = num(c.step);
    if (n === null) continue;
    seen.set(n, c.step);
  }
  return [...seen.entries()].sort((a, b) => a[0] - b[0]).map(([, s]) => s);
}

/** Источники сетки по алфавиту: усреднять по ним нельзя, значит и склеивать. */
export function sourcesOf(cells: MispricedGridCell[]): string[] {
  return [...new Set(cells.map((c) => c.source))].sort();
}

/** Клетка по трём ключам. undefined — ступень не прошла ни одна заявка. */
export function cellOf(
  cells: MispricedGridCell[],
  source: string,
  step: string,
  freshness: string,
): MispricedGridCell | undefined {
  return cells.find(
    (c) => c.source === source && c.step === step && c.freshness === freshness,
  );
}

/**
 * ROI клетки в процентах: счёт, делённый на затраты. null — делить не на что.
 *
 * Именно ROI, а не сумма. Ступени различаются числом входов на порядок, и
 * большой порог всегда «меньше потерял» — сравнение сумм выбрало бы самый
 * редкий порог просто за редкость.
 */
export function roi(c: MispricedGridCell | undefined): number | null {
  if (!c) return null;
  const pnl = num(c.pnl);
  const spent = num(c.spent);
  if (pnl === null || spent === null || spent <= 0) return null;
  return (100 * pnl) / spent;
}

/** ROI словами: «−5.8 %». Прочерк — считать было нечем. */
export function roiWord(c: MispricedGridCell | undefined): string {
  const r = roi(c);
  if (r === null) return "—";
  const sign = r > 0 ? "+" : r < 0 ? "−" : "";
  return `${sign}${Math.abs(r).toFixed(1)} %`;
}

/**
 * Доля протухших снимков среди срабатываний ступени, в процентах.
 * null — заявок на ступени не было.
 *
 * Главное число таблицы после самого ROI: оно показывает, ЧТО именно отбирает
 * порог. Рост этой доли со ступенью означает, что кромку даёт не рынок, а наша
 * задержка.
 */
export function staleShare(
  cells: MispricedGridCell[],
  source: string,
  step: string,
): number | null {
  let total = 0;
  let stale = 0;
  for (const c of cells) {
    if (c.source !== source || c.step !== step) continue;
    total += c.signals;
    if (c.freshness === "stale") stale += c.signals;
  }
  if (total === 0) return null;
  return (100 * stale) / total;
}

/**
 * Описывает ли ступень саму себя.
 *
 * Журнал усечён порогом: строки с кромкой ниже него не рождались вовсе. Значит
 * ступень НИЖЕ реально стоявшего порога описывает не «что дал бы этот порог», а
 * «что дал бы порог marginMax» — те же самые строки под чужим именем. Такую
 * ступень панель обязана пометить, а не показать наравне.
 */
export function truncated(step: string, marginMax: string): boolean {
  const s = num(step);
  const m = num(marginMax);
  if (s === null || m === null) return false;
  return s < m;
}

/**
 * Приговор сетки ОДНОГО ИСТОЧНИКА одной строкой — то, что оператор прочтёт
 * вместо таблицы.
 *
 * Источник в аргументах обязателен, а не удобство. `pm` и `model` — два ответа
 * на вопрос «стоит ли достройка своих секунд», и среднее между ними не
 * описывает ни одного: то же правило, по которому сводка режется по паре
 * «источник × роль» и никогда по ней не усредняет.
 *
 * # Две вещи, которых приговор НЕ делает
 *
 * НЕ СМОТРИТ НА ПРОТУХШИЕ СНИМКИ. Там положительна любая ступень: мы
 * «покупаем» по цене, которой на бирже уже нет. Приговор по ним описывал бы
 * нашу задержку.
 *
 * НЕ ВЫБИРАЕТ ПО МАКСИМУМУ ROI. Максимум по лестнице достаётся самой редкой
 * ступени просто за редкость: на суточном боевом журнале верхние ступени
 * набирают по девять заявок, и +61 % на них неотличимы от нуля. Ступень
 * называется, только если её запас не меньше двух сигм, — та же планка, что у
 * перевеса группы и почти решённого.
 */
export function verdict(
  cells: MispricedGridCell[],
  marginMax: string,
  source: string,
): string {
  const mine = cells.filter((c) => c.source === source);
  const steps = stepsOf(mine);
  if (steps.length === 0) return "тейкерских заявок за срок не было";

  const live = steps
    .map((step) => mine.find((c) => c.step === step && c.freshness === "live"))
    .filter((c): c is MispricedGridCell => c !== undefined)
    .map((c) => ({
      step: c.step,
      signals: c.signals,
      z: c.z,
      pnl: num(c.pnl) ?? 0,
      spent: num(c.spent) ?? 0,
    }))
    .filter((x) => x.spent > 0);
  if (live.length === 0) return "свежих снимков за срок не было: судить о деньгах не по чему";

  const withRoi = live.map((x) => ({ ...x, roi: (100 * x.pnl) / x.spent }));
  const word = (r: number) => `${r > 0 ? "+" : r < 0 ? "−" : ""}${Math.abs(r).toFixed(1)} %`;

  // Ступень, которая РЕАЛЬНО стояла, в лестнице может и отсутствовать: порог
  // назначает оператор, а лестница фиксирована. Тогда сравнивать не с чем.
  const now = withRoi.find((x) => x.step === marginMax);

  // Кандидаты — только те, чей запас дотягивает до двух сигм. Пустой список
  // здесь означает «поднимать порог не за что», а не «порог хорош».
  const solid = withRoi.filter((x) => x.z !== null && x.z >= 2);
  if (solid.length === 0) {
    const top = withRoi.reduce((a, b) => ((b.z ?? -Infinity) > (a.z ?? -Infinity) ? b : a));
    const best = withRoi.reduce((a, b) => (b.roi > a.roi ? b : a));
    return (
      `ни одна ступень не отличается от случайной: лучший запас ${zWord(top.z)} ` +
      `(${top.step}), лучший ROI ${word(best.roi)} на ${best.step} по ${best.signals} заявкам`
    );
  }
  const best = solid.reduce((a, b) => (b.roi > a.roi ? b : a));
  if (!now || best.step === now.step) {
    return (
      `на свежем стакане лучшая ступень — ${best.step}: ` +
      `${word(best.roi)} при запасе ${zWord(best.z)} по ${best.signals} заявкам`
    );
  }
  return (
    `на свежем стакане лучше стоящей ступени ${now.step} (${word(now.roi)}) ` +
    `идёт ${best.step}: ${word(best.roi)} при запасе ${zWord(best.z)} по ${best.signals} заявкам`
  );
}
